import { GridFilterModel, GridSortModel } from '@leap4/frontend';

export interface IBackendSort {
  field: string;
  direction: 'asc' | 'desc';
}

export interface IBackendFilter {
  dimension: string;
  operator: string;
  values: string[];
}

export interface IGridQueryExtras {
  sort?: IBackendSort;
  search?: string;
  gridFilters?: IBackendFilter[];
}

const MUI_TO_BACKEND_OPERATOR: Record<string, string> = {
  is: 'is',
  not: 'isNot',
  equals: 'is',
  doesNotEqual: 'isNot',
  contains: 'contains',
  doesNotContain: 'doesNotContain',
  startsWith: 'startsWith',
  endsWith: 'endsWith',
  isEmpty: 'isEmpty',
  isNotEmpty: 'isNotEmpty',
  isAnyOf: 'isAnyOf',
};

const VALUELESS_OPERATORS = new Set(['isEmpty', 'isNotEmpty']);

export const sortModelToParam = (
  model: GridSortModel | undefined
): IBackendSort | undefined => {
  const first = model && model[0];
  if (!first || !first.sort) return undefined;
  return { field: first.field, direction: first.sort === 'asc' ? 'asc' : 'desc' };
};

export const gridFilterModelToFilters = (
  model: GridFilterModel | undefined
): IBackendFilter[] => {
  if (!model || !model.items) return [];
  return model.items
    .map((item): IBackendFilter | null => {
      const operator = MUI_TO_BACKEND_OPERATOR[item.operator];
      if (!operator || !item.field) return null;
      const raw = (item as any).value;
      const values = Array.isArray(raw)
        ? raw.map(String)
        : raw === undefined || raw === null || raw === ''
          ? []
          : [String(raw)];
      if (!VALUELESS_OPERATORS.has(operator) && values.length === 0) return null;
      return { dimension: item.field, operator, values };
    })
    .filter((f): f is IBackendFilter => f !== null);
};

export const quickSearchFromModel = (
  model: GridFilterModel | undefined
): string | undefined => {
  const tokens = (model?.quickFilterValues || []).filter(Boolean).map(String);
  const search = tokens.join(' ').trim();
  return search.length > 0 ? search : undefined;
};

export const getNumericSortCompare =
  (valueParser?: (value: any) => number) =>
  (order: 'asc' | 'desc') =>
  (obj1: { data: any }, obj2: { data: any }) => {
    const val1 = obj1.data;
    const val2 = obj2.data;

    const num1 = valueParser ? valueParser(val1) : Number.parseFloat(val1);
    const num2 = valueParser ? valueParser(val2) : Number.parseFloat(val2);

    const aIsNaN = Number.isNaN(num1);
    const bIsNaN = Number.isNaN(num2);

    if (aIsNaN && bIsNaN) return 0;
    if (aIsNaN) return 1;
    if (bIsNaN) return -1;

    return order === 'asc' ? num1 - num2 : num2 - num1;
  };
